/** Round to 5 decimal places to match DB DECIMAL(10,5) precision */
export const roundDuration = (value: number): number =>
  Math.round(value * 100000) / 100000;

const WORKING_HOURS_PER_DAY = +(process.env.NEXT_PUBLIC_INSPECTOR_WORKING_HOURS ?? 8);
const SECONDS_PER_HOUR = 3600;
const ALLOCATION_DAYS_SEARCH_STEP = 0.00001;
const ALLOCATION_DAYS_SEARCH_RADIUS = 20;

/** Smallest non-zero duration in working days (one unit at DECIMAL(10,5) scale). */
export const MIN_DURATION_DAYS = 0.00001;

/** Largest duration allowed for one part when splitting into `partCount` pieces. */
export const computeMaxSinglePartDurationDays = (
  totalDurationDays: number,
  partCount: number,
): number => {
  if (partCount < 1) {
    return roundDuration(totalDurationDays);
  }
  return roundDuration(totalDurationDays - (partCount - 1) * MIN_DURATION_DAYS);
};

/**
 * Person-days total for split save/preview — matches backend validation
 * (`durationHoursToDays(activity.duration)`).
 */
export function getSplitActivityTotalDurationDays(activity: {
  duration?: number | null;
  durationDays?: number | null;
}): number {
  const hours = activity.duration != null ? Number(activity.duration) : NaN;
  if (Number.isFinite(hours) && hours > 0) {
    return durationHoursToDays(hours);
  }
  return getActivityTemplateDurationDays(activity);
}

/** Whether rounded part durations sum exactly to the activity total at 5dp. */
export function partDurationsSumMatchesTotal(
  partDurationDays: number[],
  totalDurationDays: number,
): boolean {
  if (partDurationDays.length === 0) {
    return false;
  }
  const sum = roundDuration(partDurationDays.reduce((acc, days) => acc + days, 0));
  return sum === roundDuration(totalDurationDays);
}

/** Whether split part durations can be saved (valid per-part bounds and exact total sum). */
export function canSaveSplitPartDurations(
  partDurationDays: number[],
  totalDurationDays: number,
): boolean {
  if (partDurationDays.length < 2) {
    return false;
  }

  const maxSingle = computeMaxSinglePartDurationDays(totalDurationDays, partDurationDays.length);
  const rounded = partDurationDays.map((days) => roundDuration(days));
  if (rounded.some((days) => days < MIN_DURATION_DAYS || days > maxSingle)) {
    return false;
  }

  return partDurationsSumMatchesTotal(rounded, totalDurationDays);
}

/**
 * Split a total duration into two parts at 5dp. The second part absorbs any rounding
 * remainder so the pair always sums to `totalDurationDays`.
 */
export const computeBalancedSplitDurations = (
  totalDurationDays: number,
): [number, number] => {
  const first = roundDuration(totalDurationDays / 2);
  const second = roundDuration(totalDurationDays - first);
  return [first, second];
};

/** Complementary split part so edited + returned equals `totalDurationDays` at 5dp. */
export const computeComplementarySplitDuration = (
  totalDurationDays: number,
  editedPartDays: number,
): number => roundDuration(totalDurationDays - editedPartDays);

/**
 * Split a total duration into `partCount` equal parts at 5dp. The last part absorbs any
 * rounding remainder so the parts always sum to `totalDurationDays`.
 */
export const computeEqualPartDurations = (
  totalDurationDays: number,
  partCount: number,
): number[] => {
  if (partCount < 2) {
    throw new Error('computeEqualPartDurations requires at least two parts');
  }
  const total = roundDuration(totalDurationDays);
  const base = roundDuration(total / partCount);
  const parts = Array.from({ length: partCount - 1 }, () => base);
  const headSum = roundDuration(parts.reduce((acc, days) => acc + days, 0));
  parts.push(roundDuration(total - headSum));
  return parts;
};

/**
 * When equal-split display days undershoot/overshoot `totalDurationDays` at 5dp
 * (e.g. 3 × 0.33333 = 0.99999 vs 1), bump the last part so the list sums to the total.
 */
export const reconcileDisplayDurationsToTotal = (
  displayDays: number[],
  totalDurationDays: number,
): number[] => {
  if (displayDays.length === 0) {
    return displayDays;
  }

  const target = roundDuration(totalDurationDays);
  if (!Number.isFinite(target) || target < 0) {
    return displayDays.map((days) => roundDuration(days));
  }

  const rounded = displayDays.map((days) => roundDuration(days));
  const sum = roundDuration(rounded.reduce((acc, days) => acc + days, 0));
  if (sum === target) {
    return rounded;
  }

  const headSum = roundDuration(rounded.slice(0, -1).reduce((acc, days) => acc + days, 0));
  const last = roundDuration(target - headSum);
  if (last < 0) {
    return rounded;
  }

  return [...rounded.slice(0, -1), last];
};

/**
 * After the user edits one part duration, adjust a single complementary part so the
 * list sums to `totalDurationDays` at 5dp (mirrors two-way split behaviour).
 * - Editing any non-last part updates the last part.
 * - Editing the last part updates the second-to-last part (or first when N=2).
 */
export const reconcileMultiSplitDurationsAfterEdit = (
  partDurationDays: number[],
  editedIndex: number,
  totalDurationDays: number,
): number[] => {
  if (partDurationDays.length < 2) {
    return partDurationDays;
  }

  const total = roundDuration(totalDurationDays);
  const result = partDurationDays.map((days) => roundDuration(days));
  const lastIndex = result.length - 1;

  if (editedIndex === lastIndex) {
    const pinnedLast = result[lastIndex];
    const leadingSum = roundDuration(
      result.slice(0, lastIndex - 1).reduce((acc, days) => acc + days, 0),
    );
    const complementIndex = lastIndex - 1;
    result[complementIndex] = roundDuration(total - leadingSum - pinnedLast);
  } else {
    const headSum = roundDuration(
      result.slice(0, lastIndex).reduce((acc, days) => acc + days, 0),
    );
    result[lastIndex] = roundDuration(total - headSum);
  }

  if (result.some((days) => days < MIN_DURATION_DAYS)) {
    return partDurationDays;
  }

  return result;
};

export const durationHoursToDays = (durationHours: number): number =>
  roundDuration(durationHours / WORKING_HOURS_PER_DAY);

export const durationDaysToHours = (durationDays: number): number =>
  roundDuration(durationDays * WORKING_HOURS_PER_DAY);

export const durationHoursToSeconds = (durationHours: number): number =>
  Math.round(roundDuration(durationHours) * SECONDS_PER_HOUR);

export const durationDaysToSeconds = (durationDays: number): number =>
  durationHoursToSeconds(durationDaysToHours(durationDays));

const DEFAULT_ACTIVITY_TEMPLATE_DURATION_DAYS = 1;

/** Resolve person-days for activity templates, defaulting to 1 day when unset or zero. */
export function getActivityTemplateDurationDays(activity: {
  durationDays?: number | null;
  duration?: number | null;
}): number {
  const { durationDays, duration } = activity;
  if (durationDays != null && Number.isFinite(Number(durationDays)) && Number(durationDays) > 0) {
    return roundDuration(Number(durationDays));
  }
  const hours = duration != null ? Number(duration) : NaN;
  if (Number.isFinite(hours) && hours > 0) {
    return durationHoursToDays(hours);
  }
  return DEFAULT_ACTIVITY_TEMPLATE_DURATION_DAYS;
}

/** Display label for activity template duration in person-days (e.g. `1d`). */
export function formatActivityTemplateDurationDaysLabel(activity: {
  durationDays?: number | null;
  duration?: number | null;
}): string {
  return `${getActivityTemplateDurationDays(activity)}d`;
}

/** Display label for activity duration in person-days (matches activity details modal, e.g. `1.5d`). */
export function formatActivityDurationDaysLabel(activity: {
  durationDays?: number | null;
  duration?: number | null;
}): string {
  const { durationDays, duration } = activity;
  if (durationDays != null && Number.isFinite(Number(durationDays))) {
    return `${roundDuration(Number(durationDays))}d`;
  }
  const hours = duration != null ? Number(duration) : NaN;
  if (Number.isFinite(hours) && hours > 0) {
    return `${durationHoursToDays(hours)}d`;
  }
  return '0d';
}

export const secondsToHours = (seconds: number): number => {
  return roundDuration(seconds / SECONDS_PER_HOUR);
};

export const secondsToDays = (seconds: number): number => {
  return roundDuration(seconds / SECONDS_PER_HOUR / WORKING_HOURS_PER_DAY);
};

const allocationDaysFractionalDigits = (value: number): number => {
  const fraction = value.toString().split('.')[1] ?? '';
  return fraction.replace(/0+$/, '').length;
};

const secondsForAllocationDays = (days: number): number =>
  durationHoursToSeconds(durationDaysToHours(days));

/**
 * Recover person-days from seconds when they were set via {@link durationDaysToSeconds}
 * during allocation. Prefers the shortest fractional form (e.g. 0.333 over 0.33299).
 */
export const durationSecondsToAllocationDays = (durationSeconds: number): number => {
  const roundedSeconds = Math.round(durationSeconds);
  const fallback = secondsToDays(roundedSeconds);
  const matches: number[] = [];

  for (let i = -ALLOCATION_DAYS_SEARCH_RADIUS; i <= ALLOCATION_DAYS_SEARCH_RADIUS; i++) {
    const candidate = roundDuration(fallback + i * ALLOCATION_DAYS_SEARCH_STEP);
    if (secondsForAllocationDays(candidate) === roundedSeconds) {
      matches.push(candidate);
    }
  }

  if (matches.length === 0) {
    return fallback;
  }

  return matches.sort(
    (a, b) =>
      allocationDaysFractionalDigits(a) - allocationDaysFractionalDigits(b) ||
      Math.abs(a - fallback) - Math.abs(b - fallback),
  )[0];
};
